import { useEffect, useState } from 'react';
import { Icon } from '@/lib/icons';

interface ScrollToTopProps {
  threshold?: number;
}

export function ScrollToTop({ threshold = 24 }: ScrollToTopProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let frame = 0;
    let active = window.scrollY > threshold;
    setVisible(active);

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = window.scrollY > threshold;
        if (next !== active) {
          active = next;
          setVisible(next);
        }
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [threshold]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ease-premium ${
        visible
          ? 'translate-y-0 opacity-100'
          : 'translate-y-4 opacity-0 pointer-events-none'
      }`}
    >
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Scroll to top"
        className="group/scroll relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-border/45 bg-background/80 text-muted-foreground shadow-[0_8px_32px_-12px_hsl(var(--foreground)/0.15)] backdrop-blur-xl transition-all duration-300 ease-premium hover:border-primary/25 hover:bg-background hover:text-foreground hover:shadow-[0_12px_40px_-12px_hsl(var(--primary)/0.35)] focus-ring"
      >
        <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--primary)/0.14),transparent_54%)] opacity-0 transition-opacity duration-300 group-hover/scroll:opacity-100" />
        <Icon name="chevronUp" className="relative h-4 w-4 transition-transform duration-300 group-hover/scroll:-translate-y-0.5" />
      </button>
    </div>
  );
}
